import { memo, useCallback, useMemo } from 'react';
import { Box, makeStyles, IconButton } from '@material-ui/core';
import { useSortable, SortableContext } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useDroppable } from '@dnd-kit/core';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import { SText } from '@setvi/shared/components';
import {
  QuoteSectionDTO,
  QuoteProductDTO
} from '@setvi/shared/services/react-query';
import { DragHandleIcon } from '@setvi/shared/components/sicons';
import { DraggableItem } from './draggable-item';
import { DraggableProductItem } from './draggable-product-item';
import { SECTION_DROP_PREFIX, noReflowStrategy } from './index';

const useStyles = makeStyles(theme => ({
  sectionContainer: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#EEF0F2',
    borderRadius: '16px',
    overflow: 'hidden'
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '0 12px',
    cursor: 'grab',
    '&:active': {
      cursor: 'grabbing'
    }
  },
  dragHandle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '32px',
    height: '40px',
    color: '#5D7083',
    cursor: 'grab',
    '&:active': {
      cursor: 'grabbing'
    }
  },
  headerContent: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minWidth: 0,
    padding: '8px 0'
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    lineHeight: '24px',
    color: '#333D47'
  },
  subtitle: {
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '20px',
    color: '#333D47'
  },
  expandButton: {
    padding: '8px',
    color: '#5D7083'
  },
  childrenContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    padding: '0 12px 12px 12px'
  },
  // Highlight the drop area while a product is dragged over the section.
  droppableOver: {
    outline: `2px dashed ${theme.palette.primary.main}`,
    outlineOffset: '-4px',
    borderRadius: '12px'
  },
  // Dashed insertion marker shown where the dragged item would land. Rows stay
  // put during a drag (the sortable strategy is a no-op), so the marker is a
  // zero-height overlay line and never shifts the surrounding rows.
  insertionIndicator: {
    position: 'relative',
    height: 0,
    marginLeft: '32px'
  },
  // The at-end marker is a direct flex child of the children list, so cancel the
  // 8px flex gap it would otherwise introduce above itself.
  insertionIndicatorEnd: {
    marginTop: '-8px'
  },
  insertionLine: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    height: 0,
    borderTop: `2px dashed ${theme.palette.primary.main}`,
    transform: 'translateY(-50%)',
    pointerEvents: 'none'
  },
  childItem: {
    marginLeft: '32px'
  },
  isDragging: {
    opacity: 0.5
  },
  dragOverlay: {
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)'
  },
  emptyChildren: {
    padding: '12px',
    marginLeft: '32px',
    color: '#5D7083',
    fontSize: '14px',
    fontStyle: 'italic'
  }
}));

interface SectionChildrenListProps {
  sectionChildren: QuoteProductDTO[];
  sectionId: string;
  onProductOptionsReorder?: (
    productId: string,
    children: QuoteProductDTO[]
  ) => void;
  selectedIds?: Set<string>;
  onToggleSelect?: (id: string) => void;
  /**
   * Forced highlight from the parent — true whenever the drag is anywhere
   * inside this section (over the empty drop zone or over a child). The local
   * `isOver` alone only fires over the empty zone, so it goes false once the
   * section has children and the child becomes the collision target.
   */
  isDropTarget?: boolean;
  /** Raw id under the drag (a child id or this section's drop-zone id). */
  overId?: string | null;
  /** Id of the item currently being dragged (null when idle). */
  activeId?: string | null;
}

/**
 * Renders a section's product children as sortables within the dialog's single
 * DnD context (no nested context — that's what lets products move across
 * sections). The whole area is a droppable so a product can be dropped into the
 * section even when it has no children.
 */
const SectionChildrenList = memo(
  ({
    sectionChildren,
    sectionId,
    onProductOptionsReorder,
    selectedIds,
    onToggleSelect,
    isDropTarget,
    overId,
    activeId
  }: SectionChildrenListProps) => {
    const classes = useStyles();

    const childIds = useMemo(
      () => sectionChildren.map(child => child.id || child._id),
      [sectionChildren]
    );

    const { setNodeRef, isOver } = useDroppable({
      id: `${SECTION_DROP_PREFIX}${sectionId}`
    });

    // Rows no longer slide to reveal the drop position (the sortable strategy is
    // a no-op), so the dashed marker is drawn for every drag over this section —
    // both products dragged in from outside AND reordering within the section.
    // It is never drawn at the dragged row's own position (a no-op drop).
    const dropZoneId = `${SECTION_DROP_PREFIX}${sectionId}`;
    const isDragging = !!activeId && !!overId;
    const showIndicatorBefore = (childId: string) =>
      isDragging && overId === childId && childId !== activeId;
    const showIndicatorAtEnd =
      isDragging && sectionChildren.length > 0 && overId === dropZoneId;

    return (
      <SortableContext items={childIds} strategy={noReflowStrategy}>
        <div
          ref={setNodeRef}
          className={
            isOver || isDropTarget ? classes.droppableOver : undefined
          }>
          {sectionChildren.length > 0 ? (
            <>
              {sectionChildren.map(child => {
                const childId = child.id || child._id;
                const hasProductChildren =
                  child.children && child.children.length > 0;

                return (
                  <div key={childId}>
                    {showIndicatorBefore(childId) && (
                      <Box className={classes.insertionIndicator}>
                        <span className={classes.insertionLine} />
                      </Box>
                    )}
                    <Box className={classes.childItem}>
                      {hasProductChildren ? (
                        <DraggableProductItem
                          product={child}
                          onChildrenReorder={
                            onProductOptionsReorder || (() => {})
                          }
                          selectable
                          selected={!!selectedIds?.has(childId)}
                          onToggleSelect={onToggleSelect}
                        />
                      ) : (
                        <DraggableItem
                          item={child}
                          selectable
                          selected={!!selectedIds?.has(childId)}
                          onToggleSelect={onToggleSelect}
                        />
                      )}
                    </Box>
                  </div>
                );
              })}
              {showIndicatorAtEnd && (
                <Box
                  className={`${classes.insertionIndicator} ${classes.insertionIndicatorEnd}`}>
                  <span className={classes.insertionLine} />
                </Box>
              )}
            </>
          ) : (
            <Box className={classes.emptyChildren}>
              Drag items here to add them to this section
            </Box>
          )}
        </div>
      </SortableContext>
    );
  }
);

interface DraggableSectionItemProps {
  section: QuoteSectionDTO;
  isExpanded: boolean;
  onToggle: (sectionId: string) => void;
  onProductOptionsReorder?: (
    productId: string,
    children: QuoteProductDTO[]
  ) => void;
  isDragOverlay?: boolean;
  selectedIds?: Set<string>;
  onToggleSelect?: (id: string) => void;
  isDropTarget?: boolean;
  overId?: string | null;
  activeId?: string | null;
}

export const DraggableSectionItem = memo(
  ({
    section,
    isExpanded,
    onToggle,
    onProductOptionsReorder,
    isDragOverlay = false,
    selectedIds,
    onToggleSelect,
    isDropTarget = false,
    overId,
    activeId
  }: DraggableSectionItemProps) => {
    const classes = useStyles();
    const sectionId = section.id || section._id;

    const children = useMemo(
      () => (section.children || []) as QuoteProductDTO[],
      [section.children]
    );

    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging
    } = useSortable({
      id: sectionId,
      disabled: isDragOverlay
    });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition
    };

    const handleToggle = useCallback(
      (e: React.MouseEvent) => {
        e.stopPropagation();
        onToggle(sectionId);
      },
      [sectionId, onToggle]
    );

    const hasChildren = children.length > 0;

    return (
      <div
        ref={setNodeRef}
        style={style}
        data-section-id={sectionId}
        className={`${classes.sectionContainer} ${isDragging ? classes.isDragging : ''} ${isDragOverlay ? classes.dragOverlay : ''}`}>
        <div className={classes.sectionHeader} {...attributes} {...listeners}>
          <Box className={classes.dragHandle}>
            <DragHandleIcon style={{ fontSize: '12px' }} />
          </Box>
          <Box className={classes.headerContent}>
            <SText
              title={section.name || 'Unnamed Section'}
              weight="semibold"
              className={classes.title}
              maxLines={1}
            />
            {section.note && (
              <SText
                title={section.note}
                className={classes.subtitle}
                maxLines={1}
              />
            )}
          </Box>
          {hasChildren && !isDragOverlay && (
            <IconButton
              className={classes.expandButton}
              onClick={handleToggle}
              size="small">
              {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          )}
          {hasChildren && isDragOverlay && (
            <Box className={classes.expandButton}>
              <KeyboardArrowDownIcon />
            </Box>
          )}
        </div>

        {isExpanded && !isDragOverlay && (
          <Box className={classes.childrenContainer}>
            <SectionChildrenList
              sectionChildren={children}
              sectionId={sectionId}
              onProductOptionsReorder={onProductOptionsReorder}
              selectedIds={selectedIds}
              onToggleSelect={onToggleSelect}
              isDropTarget={isDropTarget}
              overId={overId}
              activeId={activeId}
            />
          </Box>
        )}
      </div>
    );
  }
);
